<script lang="ts">
	import { derived, writable, get } from 'svelte/store';
	import LocaleSelector from '../../../../..//shared/components/common/LocaleSelector.svelte';
	import SurveyReport from '../../../../..//shared/components/common/Survey/SurveyReport.svelte';
	import NoDataAvailable from '../../../../../shared/components/common/NoDataAvailable.svelte';
	import FieldLoading from '../../../../../shared/components/common/Loading/FieldLoading.svelte';
	import FiltroGenerico from '../../../../../shared/models/Filtri/FiltroGenerico';
	import Questionario from '../../../../../shared/models/Questionario/Questionario';
	import QuestionarioRisposte from '../../../../../shared/models/Questionario/QuestionarioRisposte';
	import Sede from '../../../../../shared/models/Sede/Sede';
	import AziendaEnte from '../../../../../shared/models/AziendaEnte/AziendaEnte';
	import Response from '../../../../../shared/models/Response';
	import { locale, setupI18n } from '../../../../../shared/services/i18n';
	import Checkbox from '@smui/checkbox';
	import Button, { Label } from '@smui/button';
	import IconButton, { Icon } from '@smui/icon-button';
	import Accordion, { Content, Header, Panel } from '@smui-extra/accordion';
	import { onDestroy, tick } from 'svelte';
	import ProgressLoading from '../../../../../shared/components/common/Loading/ProgressLoading.svelte';
	import { formatPercentageWithComma } from '../../../../../shared/functions';

	console.log('🚀 QuestionarioReport - Component initialized');

	export let params = {
		id: '0',
	};

	console.log('📋 QuestionarioReport - Params:', params);

	let stringifiedQuestionarioJson = '';
	let stringifiedQuestionarioJsonUpdated = '';
	let risposteJson = [];
	let risposteJsonUpdated = null;
	let showReport = true;
	let accordionOpen = false;

	// Variabili per la gestione delle sedi con risposte
	let availableSediConRisposte = [];
	let loading_sedi = writable(false);
	let selectedSediIds = [];
	let aziendaInfo = null;
	let hasSediFilter = false;

	console.log('🔄 QuestionarioReport - Calling Questionario.get with id:', params.id);
	let [data, loading, error] = Questionario.get(params.id);
	let response: Response = { response: [new QuestionarioRisposte()] };
	let [dataRisposte, loadingRisposte, errorRisposte] = [writable(response), writable(false), null];
	let [dataQuestionarioPerReportGrafici, loadingQuestionarioPerReportGrafici, errorQuestionarioPerReportGrafici] = [writable(null), writable(false), writable(null)];

	let subscriptions = [];
	let isLoading = writable(true);

	function unsubscribeAll() {
		console.log('🧹 QuestionarioReport - Unsubscribing all subscriptions, count:', subscriptions.length);
		subscriptions.forEach(unsub => {
			if (typeof unsub === 'function') {
				unsub();
			}
		});
		subscriptions = [];
	}

	// Funzione per caricare le sedi con risposte per l'azienda
	function loadSediConRisposteForAzienda(aziendaEnteId) {
		console.log('🏢 QuestionarioReport - loadSediConRisposteForAzienda called with aziendaEnteId:', aziendaEnteId);
		if (!aziendaEnteId) {
			console.log('⚠️ QuestionarioReport - No aziendaEnteId, returning');
			return;
		}
		
		loading_sedi.set(true);
		
		// Timeout di sicurezza per evitare loading infinito
		const timeoutId = setTimeout(() => {
			console.log('⏰ QuestionarioReport - Timeout reached, forcing loading to false');
			loading_sedi.set(false);
			hasSediFilter = false;
			// Se non ci sono sedi, carica comunque i dati del report
			if (stringifiedQuestionarioJson && !reportLoadingStarted) {
				console.log('🔄 QuestionarioReport - Loading report data after timeout');
				reportLoadingStarted = true;
				loadReportData();
			}
		}, 10000); // 10 secondi
		
		// Carica prima le informazioni dell'azienda
		console.log('📡 QuestionarioReport - Loading azienda info...');
		const [dataAzienda, loadingAzienda, errorAzienda] = AziendaEnte.get(aziendaEnteId.toString());
		
		const aziendasubscription = dataAzienda.subscribe(azienda => {
			console.log('📦 QuestionarioReport - Azienda data received:', azienda);
			if (azienda) {
				aziendaInfo = azienda;
			}
		});

		// Carica le sedi con risposte per il questionario
		console.log('📡 QuestionarioReport - Loading sedi con risposte...');
		const [dataSediConRisposte, loadingSediConRisposte, errorSediConRisposte] = Questionario.getListSediConRisposte(params.id);
		
		const sediSubscription = dataSediConRisposte.subscribe(value => {
			console.log('📦 QuestionarioReport - Sedi con risposte data received:', value);
			console.log('📦 QuestionarioReport - Value type:', typeof value, 'Array?', Array.isArray(value));
			
			let sediData = null;
			
			// Gestisci diversi formati di risposta
			if (value && value.response && Array.isArray(value.response)) {
				console.log('📦 QuestionarioReport - Using value.response format');
				sediData = value.response;
			} else if (Array.isArray(value)) {
				console.log('📦 QuestionarioReport - Using direct array format');
				sediData = value;
			} else if (value && Array.isArray(value.data)) {
				console.log('📦 QuestionarioReport - Using value.data format');
				sediData = value.data;
			}
			
			console.log('📦 QuestionarioReport - Processed sediData:', sediData);
			
			if (sediData && sediData.length > 0) {
				clearTimeout(timeoutId); // Cancella il timeout
				availableSediConRisposte = sediData;
				// Per default, seleziona tutte le sedi
				selectedSediIds = availableSediConRisposte.map(sedeRisposta => sedeRisposta.sede.id);
				hasSediFilter = availableSediConRisposte.length > 0;
				loading_sedi.set(false);
				
				console.log('✅ QuestionarioReport - Sedi con risposte loaded:', {
					availableSediConRisposte: availableSediConRisposte.length,
					selectedSediIds,
					hasSediFilter
				});
				
				// AGGIUNTA: Carica automaticamente i dati dopo aver impostato le sedi
				console.log('🔄 QuestionarioReport - Auto-loading report data after sedi load');
				reportLoadingStarted = true;
				loadReportData();
			} else {
				console.log('⚠️ QuestionarioReport - No valid sedi data found, setting loading to false');
				clearTimeout(timeoutId); // Cancella il timeout
				loading_sedi.set(false);
				hasSediFilter = false;
				// Se non ci sono sedi, carica comunque i dati del report
				if (stringifiedQuestionarioJson && !reportLoadingStarted) {
					console.log('🔄 QuestionarioReport - Loading report data (no sedi filter)');
					reportLoadingStarted = true;
					loadReportData();
				}
			}
		});
		
		subscriptions.push(aziendasubscription, sediSubscription);
	}

	// Funzione per aggiornare il report con le sedi selezionate
	function updateSelectedSedi() {
		console.log('🔄 QuestionarioReport - updateSelectedSedi called, selectedSediIds:', selectedSediIds);
		isLoading.set(true);
		showReport = false;
		reportLoadingStarted = false; // Reset del flag per permettere nuovo caricamento
		
		// Ricarica i dati con il nuovo filtro
		loadReportData();
	}

	// Funzione per selezionare/deselezionare tutte le sedi
	function toggleSelectAllSedi(select) {
		console.log('✅ QuestionarioReport - toggleSelectAllSedi:', select);
		if (select) {
			selectedSediIds = availableSediConRisposte.map(sedeRisposta => sedeRisposta.sede.id);
		} else {
			selectedSediIds = [];
		}
		selectedSediIds = [...selectedSediIds];
	}

	// Funzione principale per caricare i dati del report
	function loadReportData() {
		console.log('📊 QuestionarioReport - loadReportData called');
		console.trace('loadReportData call stack');
		
		const questionarioData = get(data) as Questionario;
		console.log('📋 QuestionarioReport - questionarioData:', questionarioData);
		if (!questionarioData) {
			console.log('⚠️ QuestionarioReport - No questionarioData, returning');
			return;
		}

		// Marca che il caricamento è iniziato
		reportLoadingStarted = true;

		let filters: FiltroGenerico = {
			questionariId: questionarioData.id > 0 ? [questionarioData.id] : [parseInt(params.id)],
		};

		if (hasSediFilter) {
			filters.sediId = [...selectedSediIds];
		}

		console.log('🔍 QuestionarioReport - Filters for risposte:', filters);

		unsubscribeAll();

		console.log('📡 QuestionarioReport - Loading risposte...');
		const [newDataRisposte, newLoadingRisposte, newErrorRisposte] = QuestionarioRisposte.getList(filters);
		
		const loadingSubscription = newLoadingRisposte.subscribe(loading => {
			console.log('⏳ QuestionarioReport - Risposte loading state:', loading);
			if (!loading) {
				setTimeout(() => { 
					console.log('⏱️ QuestionarioReport - Setting isLoading to false after timeout');
					isLoading.set(loading);
				}, 500);
			} else {
				isLoading.set(loading);
			}
		});

		const risposteSubscription = newDataRisposte.subscribe(async value => {
			// 1) salto emissioni che non contengono response (placeholder iniziali)
			if (!value || !Array.isArray(value.response)) {
				console.log('⏭️ QuestionarioReport - Payload risposte senza response, skippo');
				return;
			}

			// 2) salto emissione placeholder con response[0].id <= 0
			if (value.response.length === 1 && value.response[0].id <= 0) {
				console.log('⏭️ QuestionarioReport - Placeholder risposte (id=0), skippo');
				return;
			}

			// da qui in poi ho dati "veri"
			console.log('📦 QuestionarioReport - Risposte data "vera" ricevuta:', value);

			if (value.response.length > 0) {
				risposteJson = value.response.map(r => JSON.parse(r.questionarioRisposteJson));
				console.log('✅ Parsed risposte:', risposteJson.length);
				prepareReportData(stringifiedQuestionarioJson, risposteJson);
			} else {
				console.log('❌ Nessuna risposta effettiva trovata');
				risposteJson = [];
				risposteJsonUpdated = [];
				prepareReportData(stringifiedQuestionarioJson, []);
			}
		});

		subscriptions.push(loadingSubscription, risposteSubscription);
	}

	async function prepareReportData(questionarioString, risposteArray) {
		console.log('🎨 QuestionarioReport - prepareReportData called with:', {
			hasQuestionario: !!questionarioString,
			risposteCount: risposteArray?.length || 0
		});
		console.trace('prepareReportData call stack');
		
		// Se il questionarioString è vuoto, non fare la chiamata
		if (!questionarioString) {
			console.log('⚠️ QuestionarioReport - No questionarioString, returning');
			isLoading.set(false);
			return;
		}
		
		isLoading.set(true);
		
		// Se non ci sono risposte, imposta direttamente i dati senza chiamare il servizio
		if (!risposteArray || risposteArray.length === 0) {
			console.log('⚡ QuestionarioReport - No risposte, setting data directly');
			showReport = false;
			stringifiedQuestionarioJsonUpdated = questionarioString;
			risposteJsonUpdated = [];
			await tick();
			showReport = true;
			isLoading.set(false);
			return;
		}
		
		// Chiama il servizio solo se ci sono sia il questionario che le risposte
		console.log('📡 QuestionarioReport - Calling prepareSurveyForGraphicReports service');
		const [reportData, reportLoading, reportError] = Questionario.prepareSurveyForGraphicReports(
			questionarioString,
			JSON.stringify(risposteArray)
		);
		
		const loadingSubscription = reportLoading.subscribe(loading => {
			console.log('⏳ QuestionarioReport - Report preparation loading:', loading);
			isLoading.set(loading);
		});
		
		const dataSubscription = reportData.subscribe(async value => {
			console.log('📦 QuestionarioReport - Report data received:', value);
			if (value && value.response) {
				showReport = false;
				
				if (value.response.questionarioJsonStringified) {
					stringifiedQuestionarioJsonUpdated = value.response.questionarioJsonStringified;
					console.log('✅ QuestionarioReport - Updated questionario JSON');
				}
				
				if (value.response.risposte) {
					risposteJsonUpdated = [...value.response.risposte];
					console.log('✅ QuestionarioReport - Updated risposte:', risposteJsonUpdated.length);
				}
				
				await tick();
				showReport = true;
				console.log('👁️ QuestionarioReport - Report visible');
			}
		});
		
		const errorSubscription = reportError.subscribe(err => {
			if (err) {
				console.error('❌ QuestionarioReport - Error in report preparation:', err);
				isLoading.set(false);
			}
		});
		
		subscriptions.push(loadingSubscription, dataSubscription, errorSubscription);
	}

	// Subscription principale per il questionario
	data.subscribe((value) => {
		if (value) {
			console.log('📥 QuestionarioReport - Main data subscription triggered:', value);
			if (!value || value.id <= 0 || !value.questionarioJson) {
				console.log('⚠️ QuestionarioReport - Dati non validi, salto il caricamento');
				return;
			}
			stringifiedQuestionarioJson = value.questionarioJson;
			console.log('📝 QuestionarioReport - Set stringifiedQuestionarioJson');
			
			// Carica le sedi con risposte se il questionario ha un'azienda associata
			if (value.aziendaEnteId) {
				console.log('🏢 QuestionarioReport - Has aziendaEnteId, loading sedi con risposte');
				loadSediConRisposteForAzienda(value.aziendaEnteId);
			} else {
				// Se non ha azienda associata, carica direttamente i dati
				console.log('🚀 QuestionarioReport - No aziendaEnteId, loading data directly');
				hasSediFilter = false;
				if (!reportLoadingStarted) {
					reportLoadingStarted = true;
					loadReportData();
				}
			}
		}
	});

	// Questa variabile tiene traccia se il caricamento è già stato avviato
	let reportLoadingStarted = false;

	// Gestisce il caricamento dei dati quando le sedi sono pronte (solo per debugging)
	const dataAndDataRisposte = derived(
		[loading, loadingRisposte, loading_sedi], 
		([$loading, $loadingRisposte, $loadingSedi]) => {
			console.log('🔗 QuestionarioReport - Derived store updated:', {
				loading: $loading,
				loadingRisposte: $loadingRisposte,
				loadingSedi: $loadingSedi,
				reportLoadingStarted
			});
			
			return { loading: $loading, loadingRisposte: $loadingRisposte, loadingSedi: $loadingSedi };
		}
	);

	// Sottoscrizione del derived store solo per debug (non fa più caricamento automatico)
	dataAndDataRisposte.subscribe((value) => {
		console.log('📊 QuestionarioReport - dataAndDataRisposte subscription:', value, {
			hasSediFilter,
			hasQuestionarioJson: !!stringifiedQuestionarioJson,
			reportLoadingStarted
		});
	});

	onDestroy(() => {
		console.log('💥 QuestionarioReport - Component destroyed');
		unsubscribeAll();
	});

	$: {
		console.log('🔄 QuestionarioReport - Reactive statement triggered for dataQuestionarioPerReportGrafici:', $dataQuestionarioPerReportGrafici);
		if($dataQuestionarioPerReportGrafici) {
			if ($dataQuestionarioPerReportGrafici?.response?.questionarioJsonStringified) {
				stringifiedQuestionarioJsonUpdated = $dataQuestionarioPerReportGrafici?.response?.questionarioJsonStringified;
				console.log('📝 QuestionarioReport - Updated questionario from reactive');
			}
			if ($dataQuestionarioPerReportGrafici?.response?.risposte) {
				risposteJsonUpdated = [...$dataQuestionarioPerReportGrafici?.response?.risposte];
				console.log('📝 QuestionarioReport - Updated risposte from reactive:', risposteJsonUpdated.length);
			}
		}
	}

	console.log('✅ QuestionarioReport - Component setup complete');
</script>

{#if Boolean($isLoading) === true || Boolean($loading) === true || !showReport || risposteJsonUpdated == null}
  <ProgressLoading />
{:else}
  <div class="text-end pb-4">
    <LocaleSelector
      value="{$locale}"
      on:locale-changed="{(e) => setupI18n({ withLocale: e.detail })}"
    />
  </div>

  {#if hasSediFilter}
    <Accordion>
      <Panel square variant="outlined" color="primary" extend bind:open={accordionOpen}>
        <Header>
          Filtra per sede
          <IconButton slot="icon" toggle pressed={accordionOpen}>
            <Icon class="material-icons" on>unfold_less</Icon>
            <Icon class="material-icons">unfold_more</Icon>
          </IconButton>
        </Header>
        <Content>
          {#if $loading_sedi}
            <FieldLoading />
          {:else if availableSediConRisposte.length === 0}
            <p>Nessuna sede disponibile per questa azienda</p>
          {:else}
					<div class="flex justify-end mb-4">
						<Button
							on:click="{updateSelectedSedi}"
							type="button"
							variant="raised"
							class="w-auto ml-4"
							disabled={selectedSediIds.length === 0}
						>
							<Label class="text-sm">
								Aggiorna report
							</Label>
						</Button>
						{#if selectedSediIds.length === 0}
							<p class="mt-1 text-xs text-red-500">
								Seleziona almeno una sede per aggiornare.
							</p>
						{/if}
					</div>
					<div class="mb-4">
						<h4 class="mb-2 text-lg font-semibold">
							{aziendaInfo?.nomeAziendaEnte || 'Azienda'}
						</h4>
						<p class="mb-3 text-sm text-gray-600">
							Seleziona le sedi da includere nel report:
						</p>
						
						<div class="mb-3">
							<Checkbox 
								checked={availableSediConRisposte.length > 0 && selectedSediIds.length === availableSediConRisposte.length}
								indeterminate={selectedSediIds.length > 0 && selectedSediIds.length < availableSediConRisposte.length}
								on:change={(e) => toggleSelectAllSedi(e.target.checked)}
							/>
							<span class="ml-2 text-sm font-medium">Seleziona tutte le sedi</span>
						</div>
					</div>
					
					<div class="max-h-64 overflow-auto border rounded-lg">
						<table class="min-w-full text-sm">
							<thead class="bg-gray-50">
								<tr>
									<th class="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
										Denominazione
									</th>
									<th class="px-4 py-2 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">
										Risposte ricevute
									</th>
									<th class="px-4 py-2 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">
										Percentuale risposte
									</th>
									<th class="px-4 py-2 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">
										Seleziona
									</th>
								</tr>
							</thead>
							<tbody class="bg-white divide-y divide-gray-200">
								{#each availableSediConRisposte as sedeRisposta}
								<tr class="hover:bg-gray-50">
									<td class="px-4 py-2 text-sm font-medium text-gray-900">
										{sedeRisposta.sede?.denominazione ?? ''}
									</td>
									<td class="px-4 py-2 text-sm text-center text-gray-500">
										{sedeRisposta.numeroRisposteRicevute} / {sedeRisposta.numeroTotaleLavoratori}
									</td>
									<td class="px-4 py-2 text-sm text-center text-gray-500">
										{formatPercentageWithComma(sedeRisposta.percentualeRisposte)}%
									</td>
									<td class="px-4 py-2 text-center">
										<Checkbox
											checked={selectedSediIds.includes(sedeRisposta.sede.id)}
											on:change={(e) => {
												if (e.target.checked) {
													selectedSediIds = [...selectedSediIds, sedeRisposta.sede.id];
												} else {
													selectedSediIds = selectedSediIds.filter(id => id !== sedeRisposta.sede.id);
												}
											}}
										/>
									</td>
								</tr>
								{/each}
							</tbody>
						</table>
					</div>
					
					<div class="flex items-center justify-between mt-4">
						<span class="text-sm text-gray-600">
							{selectedSediIds.length} sedi selezionate su {availableSediConRisposte.length} disponibili
						</span>
					</div>
				{/if}
        </Content>
      </Panel>
    </Accordion>
  {/if}

  {#if stringifiedQuestionarioJsonUpdated !== ''}
    <SurveyReport
      bind:stringifiedQuestionarioJson={stringifiedQuestionarioJsonUpdated}
      bind:risposteJson={risposteJsonUpdated}
    />
  {:else}
    <NoDataAvailable />
  {/if}
{/if}